import React, { type ReactElement } from 'react';
import { CommonButton, CommonButtonV1 } from 'gemlux-common-components';
import { Theme, styled as materialStyled } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import AlertSvg from '../../assets/img/warning-outline.svg';
import CheckTickSvg from '../../assets/img/check-tick.svg';
import ErrorSvg from '../../assets/img/warning-red.svg';
import { theme } from '../../theme/theme';

const ModalContainer = materialStyled(Container)((_: { theme: Theme }) => ({
  backgroundColor: theme.palette.white.main,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  boxShadow: '0px 6px 12px 0px rgba(51, 51, 51, 0.08);',
  border: '1px solid #B6C1CA',
  borderRadius: '12px',
  width: '300px',
}));
const CircleBox = materialStyled(Box)(() => ({
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

interface AlertProps {
  message: ReactElement | string;
  title?: ReactElement | string;
  type: 'success' | 'warning' | 'error';
  onClickOk?: () => void;
  onClickCancel?: () => void;
  okText?: string;
  cancelText?: string;
}

const getColors = (type: 'success' | 'warning' | 'error') => {
  switch (type) {
    case 'success':
      return theme.palette.shades.successBrightLight;
    case 'warning':
      return theme.palette.shades.warningBrightLight;
    case 'error':
      return theme.palette.shades.errorBrightLight;
    default:
      return theme.palette.shades.successBrightLight;
  }
};

const getSvgIcon = (type: 'success' | 'warning' | 'error') => {
  switch (type) {
    case 'success':
      return CheckTickSvg;
    case 'warning':
      return AlertSvg;
    case 'error':
      return ErrorSvg;
    default:
      return CheckTickSvg;
  }
};

const AlertModel = ({
  message,
  type,
  okText,
  cancelText,
  onClickCancel,
  onClickOk,
  title,
}: AlertProps): ReactElement => (
  <ModalContainer>
    <CircleBox sx={{ backgroundColor: getColors(type) }}>
      <Box component="img" width="23px" src={getSvgIcon(type)} />
    </CircleBox>
    {title && (
      <Typography variant="title5Bold" sx={{ textAlign: 'center', marginTop: '24px' }}>
        {title}
      </Typography>
    )}
    <Typography variant="tableheadBold" sx={{ textAlign: 'center', marginTop: '24px' }}>
      {message}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-evenly', width: '100%', flexDirection: 'column' }}>
      {onClickOk && (
        <CommonButtonV1
          variant="contained"
          sx={{
            width: '100%',
            backgroundColor: theme.palette.shades.lightBlueColor,
            marginTop: '10px',
          }}
          onClick={onClickOk}
        >
          {okText || 'Ok'}
        </CommonButtonV1>
      )}
      {onClickCancel && (
        <CommonButtonV1
          variant="outlined"
          sx={{
            marginTop: '10px',
            width: '100%',
            color: '#5261FE',
            border: `1px solid ${theme.palette.shades.lightBlueColor}`,
          }}
          backgroundColor=" transparent"
          onClick={onClickCancel}
        >
          {cancelText || 'Cancel'}
        </CommonButtonV1>
      )}
    </Box>
  </ModalContainer>
);

export default AlertModel;
